import { queryProcedureResult } from 'iguazu-rpc';
import { addErrorToReport } from '@americanexpress/one-app-ducks';
import { oneDataFetchye } from '@americanexpress/fetchye-amex';
import {
  BUSINESS_CHECKING_ACCOUNT,
  loadProductsRegistry,
} from 'axp-product-ducks';
import { v4 as uuidv4 } from 'uuid';
import { generateOneLoginDestinationUrl } from '@americanexpress/banking-common';
import redirect from './redirect';
import {
  APPLICATION_EXPECTED_RESPONSE_CODES,
  BUSINESS_SAVINGS_ACCOUNT,
  IN_PROGRESS_APPLICATION_STATUSES,
  READ_BUSINESS_CHECKING_APPLICATION,
  READ_CUSTOMER_PRODUCTS_V2_FUNCTION,
  READ_TWO_FACTOR_AUTHENTICATION_FOR_USER_V1,
  ROLES,
  ROUTES,
} from '../constants';

const onEnterBusinessBankingAccounts =
  (store) => async (nextState, replace, callback) => {
    const correlationId = uuidv4();

    const redirectToLogin = () => {
      const browserOrigin = store
        .getState()
        .getIn(['browser', 'location', 'origin']);
      const oneLoginUrl = store.getState().getIn(['config', 'oneLoginUrl']);

      const loginUrl = generateOneLoginDestinationUrl(
        oneLoginUrl,
        browserOrigin,
        nextState.location.pathname
      );
      redirect(loginUrl, replace);
    };

    try {
      const readMFAData = await store.dispatch(
        queryProcedureResult({
          procedureName: READ_TWO_FACTOR_AUTHENTICATION_FOR_USER_V1,
          headers: {
            'one-data-correlation-id': correlationId,
          },
          args: {
            timeout: 6e3,
          },
        })
      ).promise;

      if (readMFAData.status && readMFAData.status > 400) {
        redirectToLogin();
      }

      await store.dispatch(loadProductsRegistry());

      const productsResult = await store.dispatch(
        queryProcedureResult({
          procedureName: READ_CUSTOMER_PRODUCTS_V2_FUNCTION,
          headers: {
            'one-data-correlation-id': correlationId,
          },
          args: {
            timeout: 6e3,
          },
        })
      ).promise;

      // iguazu-rpc will return status only when it is not an http 200
      if (productsResult.status && productsResult.status !== 401) {
        store.dispatch(
          addErrorToReport('ClientReportedError', {
            collectionMethod: 'bca-onEnter',
            hook: 'accounts',
            failedRequestCorrelationId: correlationId,
            oneDataFunction: 'ReadCustomerProducts.v2',
            moduleID: 'banking-business-checking-root',
            team: 'business-checking-ui-engineering',
          })
        );
        redirect(ROUTES.ERROR, replace);
      }

      // user is unauthorized
      if (productsResult.status === 401) {
        redirectToLogin();
      }

      // user has no business checking accounts
      if (
        Array.isArray(productsResult) &&
        productsResult.find((account) =>
          [BUSINESS_CHECKING_ACCOUNT, BUSINESS_SAVINGS_ACCOUNT].includes(
            account.type
          )
        ) == null
      ) {
        const readApplicationsResponse = await store.dispatch(
          oneDataFetchye(READ_BUSINESS_CHECKING_APPLICATION, {
            body: {},
            headers: {
              'one-data-correlation-id': correlationId,
            },
          })
        );

        const {
          status: readApplicationsStatus,
          body: readApplicationsResponseBody,
        } = readApplicationsResponse.data;
        if (
          !APPLICATION_EXPECTED_RESPONSE_CODES.includes(readApplicationsStatus)
        ) {
          store.dispatch(
            addErrorToReport('ClientReportedError', {
              collectionMethod: 'bca-onEnter',
              hook: 'accounts',
              failedRequestCorrelationId: correlationId,
              oneDataFunction: READ_BUSINESS_CHECKING_APPLICATION,
              moduleID: 'banking-business-checking-root',
              team: 'business-checking-ui-engineering',
            })
          );
          redirect(ROUTES.ERROR, replace);
        } else {
          const inProgressBacApplication =
            readApplicationsResponseBody?.applications?.find((application) =>
              IN_PROGRESS_APPLICATION_STATUSES.includes(
                application?.applicationStatus?.currentPhase?.status
              )
            );
          const getStakeholderType = (application) =>
            application?.stakeholders?.[0]?.stakeholderType;

          const rsvpCode = inProgressBacApplication?.rsvpCode;

          if (inProgressBacApplication) {
            if (
              getStakeholderType(inProgressBacApplication) === ROLES.VIEW_ONLY
            ) {
              redirect(
                ROUTES.ACCOUNT_REGISTRATION.replace(':rsvpCode', rsvpCode),
                replace
              );
            } else if (
              getStakeholderType(inProgressBacApplication) === ROLES.ADMIN
            ) {
              redirect(
                ROUTES.ADDITIONAL_USER_ONBOARDING.replace(
                  ':rsvpCode',
                  rsvpCode
                ),
                replace
              );
            } else {
              redirect(ROUTES.BLUEPRINT_APPLY, replace);
            }
          } else {
            redirect(ROUTES.BLUEPRINT_APPLY, replace);
          }
        }
      }
    } catch (err) {
      store.dispatch(
        addErrorToReport(err, {
          collectionMethod: 'bca-onEnter',
          hook: 'accounts',
          failedRequestCorrelationId: correlationId,
          moduleID: 'banking-business-checking-root',
          team: 'business-checking-ui-engineering',
        })
      );
      redirect(ROUTES.ERROR, replace);
    }
    callback();
  };

export default onEnterBusinessBankingAccounts;
